import { MappingsItemSettings } from 'src/app/nav-processing/mappings/mappings.model';
import { VoicePreference } from '../voice/voice.model';


export enum Access {
    Hidden = 'hidden',
    HiddenAdvanced = 'hiddenAdvanced',
    HiddenSupport = 'hiddenSupport',
    Required = 'required',
    Optional = 'optional'
}

export enum SelectorType {
    Switch = 'switch',
    Slider = 'slider',
    SliderMS = 'sliderMS',
    Dropdown = 'dropdown',
    Range = 'range',
    Button = 'button',
}

export enum CursorType {
    Scan = 'scan',
    EyeTracker = 'eyeTracker',
    GazeTracker = 'gazeTracker',
    Face2D = 'face2D',
    Face1D = 'face1D',
    Touch = 'touch',
    Mouse = 'mouse'
}

export enum KeyboardLayouts {
    AlphabeticalOrder = 'alphabeticalOrder',
    ScanOptmized = 'scanOptmized',
    EyeTracker = 'eyeTracker',
    Reduced = 'reduced'
}
/**
 * Cada bundle representa um conjunto de configurações que podem ser aplicadas ao sistema de navegação.
 * Esse conjunto deve ser especializado para o tipo de mecanismo de navegação que está sendo utilizado.
 * Ex: Para o mecanismo eyeTracker, o bundle pode conter configurações específicas para o rastreador de olhos.
 * mas cada rastreador tem suas peculiaridades então cada um terá um bundle diferente, apesar de serem do mesmo
 * tipo e terem muitas opçoes similares. O que eles terão em comum será o cursorType.
 * 
 */
export interface NavSettingsBundle extends NavSettingsBundleItem {
    name: string; // RO; cabeça 2D; Cabeça + Boca; Boca 1D
    version: number; // Version to control the updates when new settings are added or changed
    cursorType: CursorType; // The mechanism / scheme of navigation that selects which cursor overlay is used
    bundleOptions?: Array<SettingsBlock>; // The options that can be set for the entire bundle
    bundleOptionsState?: NavSettingsBundleState; // The state of the bundle options
    availableNavSettingsBundles?: Array<NavSettingsBundle>; // The list of bundles that can be added by the user
    availableDirections?: Array<DirectionDescriptor>; // The list of directions that can be added by the user
    availableSwitches?: Array<SwitchDescriptor>; // The list of switches that can be added by the user
    directions: Array<DirectionDescriptor>; // The list of directions that are currently active
    switches: Array<SwitchDescriptor>; // The list of switches that are currently active
    generalSettings?: Array<SettingsBlock>; // The general settings that can be added to the global settings if this bundle is active
    exampleImage?: string; // The image that shows an example of the bundle in action
}

export interface NavSettingsBundleState {
    // Head 2D / 1D
    navGuardTimeout?: number;
    cursorFilterTimeout?: number;
    // Touch
    touchRepetitionFilterDuration?: number;
    touchFilterDuration?: number;
    // Scan
    scanInterval?: number; // MS
    scanAutoPauseInterval?: number; // MS
    scanAutoPauseLoops?: number;
    scanOptimizeKeyboard?: boolean; // Optimize the keyboard layout for scanning
    // Selection
    dwellDuration?: number;
    speakPositioningTips?: boolean;
    selectRawFeedback?: boolean;
    // Peye
    peyeCalibrationFile?: string; 
    // Rastreador Ocular
    rastreadorCalibrationData?: Array<number>;
    rastreadorCalibrationDate?: number;
    rastreadorImage?: boolean;
    rastreadorGlasses? : boolean;
    // Gaze cursor
    gazeLeakRate?: number; // 0..1 leak speed relative to fill (per same time unit)
    gazeDebugUseMouse?: boolean; // if true, use mouse pointer as gaze for testing
    gazePointVisualization?: boolean; // if true, show gaze point indicator
    // If true, hide the dwell ring/border used by the gaze cursor visuals
    gazeHideDwellBorder?: boolean;
}

export interface GeneralSettingsState {
    voicePrimary?: VoicePreference;
    voiceVolumePrimary?: number;
    voiceSecondary?: VoicePreference;
    voiceVolumeSecondary?: number;
    voicesAvailable?: Array<VoicePreference>;
    keyboardLayout?: KeyboardLayouts;
}

export interface SwitchDescriptor extends NavSettingsBundleItem{
    name: string;
    default?: boolean;
    mappings: Array<MappingsItemSettings>;
    //defaults?: Array<MappingsItemSettings>;
    options?: Array<SettingsBlock>;
}

export interface DirectionDescriptor extends NavSettingsBundleItem {
    name: string;  // ROx; ROy; Cabeça Vertical
    mappings: Array<MappingsItemSettings>; // The state variables are the mapping settings
    //defaults?: Array<MappingsItemSettings>; // The default settings for the mappings
    options?: Array<SettingsBlock>; // Options that modify the mappings
}

export interface SettingsBlock {
    name?: string;
    settings: Array<SettingDescriptor>;
}

export interface SettingDescriptor extends NavSettingsBundleItem {
    name: string;
    settingKey: string;
    feedbackKey?: string;
    selectorType: SelectorType;
    selectorOptionList?: Array<OptionItem>;
    default?: any | {lower: number, upper: number};
    extremes?: {lower: number, upper: number};
}

export interface OptionItem {
    label: string;
    value: any;
}

export interface navBundleCondition {
    localTest: (bundle: NavSettingsBundleItem) => boolean;
}

export enum ItemID {

    // Bundles
    NavBundleEyeTrackerPeye = 'eyeTrackerPeye',
    NavBundleRastreadorOcular = "NavBundleRastreadorOcular",
    NavBundleTobii = 'eyeTracker',
    NavBundleHead2DHV = 'head2DHV',
    NavBundleHead1DHMouth1DV = 'head1DHMouth1DV',
    NavBundleHead1DH = 'head1DH',
    NavBundleHead1DV = 'head1DV',
    NavBundleMouth1D = 'mouth1D',
    NavBundleScan = 'scan',
    NavBundleTouch = 'touch',
    NavBundleGazeTracker = 'gazeTracker',

    // Directions
    DirTobiiEyeTrackerH = 'dirTobiiEyeTrackerH',
    DirTobiiEyeTrackerV = 'dirTobiiEyeTrackerV',
    DirPeyeEyeTrackerH = 'dirPeyeEyeTrackerH',
    DirPeyeEyeTrackerV = 'dirPeyeEyeTrackerV',
    DirHeadP = 'dirHeadP', // Head Pitch
    DirHeadY = 'dirHeadY', // Head Yaw
    DirMouthOpen = 'dirMouthOpen',
    DirGazeH = 'dirGazeH',
    DirGazeV = 'dirGazeV',

    // Switches
    SwitchHeadPitch = 'SwitchHeadPitch',
    SwitchHeadPitchUp = 'SwitchHeadPitchUp',
    SwitchHeadYaw = 'SwitchHeadYaw',
    SwitchHeadYawLeft = 'SwitchHeadYawLeft',
    SwitchHeadRoll = 'SwitchHeadRoll',
    SwitchHeadRollLeft = 'SwitchHeadRollLeft',
    SwitchMouthSmile = 'switchMouthSmile',
    SwitchEyeBlinkAny = 'switchEyeBlinkAny',
    SwitchEyeBlinkBoth = 'switchEyeBlinkBoth',
    SwitchEyeBlinkLeft = 'switchEyeBlinkLeft',
    SwitchEyeBlinkRight = 'switchEyeBlinkRight',
    SwitchEyeBlinkLeftOnly = 'switchEyeBlinkLeftOnly',
    SwitchEyeBlinkRightOnly = 'switchEyeBlinkRightOnly',
    SwitchOpenMouth = 'switchOpenMouth',
    SwitchCloseMouth = 'switchCloseMouth',
    SwitchMouthPuck = 'switchMouthPuck',
    SwitchDwell = 'switchDwell',
    SwitchEyeBrowUp = 'switchEyeBrowUp',
    SwitchEyeBrowDown = 'switchEyeBrowDown',
    SwitchMouseClickLeft = 'switchMouseClickLeft',
    
    // Mapping Options
    OptResolution = 'optResolution',
    OptRange = 'optRange',
    OptHysteresis = 'optHysteresis',
    OptSound = 'optSound',
    OptDuration = 'optDuration',
    OptSoundVolume = 'optSoundVolume',
    OptAction = 'optAction',
    OptEnabled = 'optEnabled',
    OptMovement = 'optMovement',

    // Cursor Settings
    OptNavGuardTime = 'optNavGuardTime',
    OptNavFilterTime = 'optNavFilterTime',
    OptNavScanInterval = 'optScanInterval',
    OptNavScanAutoPauseInterval = 'optNavScanAutoPauseInterval',
    OptNavScanAutoPauseLoops = 'optNavScanAutoPauseLoops',
    OptNavTouchDuration = 'optNavTouchDuration',
    OptNavTouchRepetition = 'optNavTouchRepetition',
    OptSpeakPositioningTips = 'optSpeakPositioningTips',
    OptNavSelectRawFeedback = 'optSelectRawFeedback',
    OptNavOptimizeKeyboardScan = 'optOptimizeKeyboardScan',
    OptRastreadorImage = 'optRastreadorImage',
    OptCalibrationButton = 'optCalibrationButton',
    OptRastreadorGlasses = 'optRastreadorGlasses',
    OptGazeDebugUseMouse = 'optGazeDebugUseMouse',
    OptNavDwellDuration = 'optDwellDuration',
    OptGazePointVisualization = 'optGazePointVisualization',
    OptGazeHideDwellBorder = 'optGazeHideDwellBorder',

    // General Settings
    OptGeneralKeyboardLayout = 'optGeneralKeyboardLayout',
}

interface NavSettingsBundleItem {
    id: ItemID;
    access?: Access;
    condition?: navBundleCondition;
}

